import { AlertOctagon, ShieldX } from 'lucide-react';

/**
 * Patch v18.0 — Banned Account Banner.
 *
 * Rendered on every member-facing surface (Dashboard, Withdraw, Purchase)
 * when the current user has ``is_flagged === true`` on their profile
 * document. Two visual states depending on whether a God Admin has
 * clicked Delete on the Cheaters subpage:
 *
 *   * is_flagged  && !is_deleted_at  → yellow "under review" state
 *   * is_flagged  &&  is_deleted_at  → red "banned + purged" state
 *
 * The banner is display-only. All withdrawal/purchase blocking is
 * enforced server-side (see wallet.py ACCOUNT_FLAGGED gate). This
 * component just makes the block visible to the member.
 *
 * Props
 * -----
 * me : object   — the member's profile document from /api/me. Must
 *                 include ``is_flagged`` and ``is_deleted_at`` fields.
 *
 * Returns null when the member is not flagged so the parent can render
 * this component unconditionally at the top of any page.
 */
export const BannedBanner = ({ me }) => {
  if (!me?.is_flagged) return null;
  const isDeleted = Boolean(me.is_deleted_at);
  const Icon = isDeleted ? ShieldX : AlertOctagon;

  const palette = isDeleted
    ? {
        background: 'linear-gradient(180deg, rgba(220,38,38,0.14), rgba(220,38,38,0.06))',
        border: '1px solid rgba(248,113,113,0.55)',
        iconBg: 'rgba(220,38,38,0.20)',
        iconColor: '#fca5a5',
        titleColor: '#fca5a5',
      }
    : {
        background: 'linear-gradient(180deg, rgba(255,182,18,0.14), rgba(255,182,18,0.06))',
        border: '1px solid rgba(255,182,18,0.55)',
        iconBg: 'rgba(255,182,18,0.20)',
        iconColor: '#FFE08A',
        titleColor: '#FFE08A',
      };

  return (
    <div
      className="rounded-2xl p-4 md:p-5 flex items-start gap-3"
      style={{ background: palette.background, border: palette.border }}
      role="alert"
      data-testid="member-banned-banner"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: palette.iconBg }}>
        <Icon className="w-5 h-5" strokeWidth={2.2} style={{ color: palette.iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-[15px] md:text-[16px] leading-tight" style={{ color: palette.titleColor }}>
          {isDeleted ? 'Your account has been banned.' : 'Your account has been suspended pending review.'}
        </div>
        <div className="text-xs md:text-[13px] mt-1 leading-snug text-[var(--sc-text-dim)]">
          {isDeleted ? (
            <>All withdrawals are blocked. All positions and sub packs have been removed from your account. If you believe this is an error, contact support.</>
          ) : (
            <>Withdrawals and purchases are temporarily blocked while your account is under review. If you believe this is an error, contact support.</>
          )}
        </div>
      </div>
    </div>
  );
};

export default BannedBanner;
